/** Millimetres per legacy unit. */
const MM = 10;

/** `m_dwParticleMaxNum` (`GameLib/SnowEnvironment.cpp:339`). */
export const SNOW_MAX_FLAKES = 3000;
/** Flakes created per `Deform` while snowing (`SnowEnvironment.cpp:79`). */
const SPAWN_PER_FRAME = 10;
/** How far along the view the field is centred (`SnowEnvironment.cpp:56`). */
const VIEW_OFFSET_MM = 3500 * MM;
/** `c_fSnowDistance` (`GameLib/SnowParticle.cpp:4`). */
const SNOW_DISTANCE_MM = 70000 * MM;
/** A flake this far below the centre is dropped (`SnowParticle.cpp:27`). */
const FLOOR_MM = 500 * MM;

/** x, y, z in millimetres, then u, v: one vertex of a flake quad. */
export const SNOW_FLAKE_FLOATS = 5;

export type Vec3 = readonly [number, number, number];

/** The camera's look, up and right axes, unit length, in world space. */
export interface SnowBasis {
  readonly view: Vec3;
  readonly up: Vec3;
  readonly cross: Vec3;
}

interface Flake {
  x: number;
  y: number;
  z: number;
  fallMmPerS: number;
  halfMm: number;
  period: number;
  radian: number;
  crossX: number;
  crossY: number;
}

/** `CSnowEnvironment`'s particle list: a world-space field of falling flakes
 *  around a point ahead of the camera, topped up while snowing and left to
 *  drain when not (`SnowEnvironment.cpp:35-86`, `SnowParticle.cpp:19-78`).
 *  `random` answers [0, 1), the `random()` fraction `frandom` scales
 *  (`EterBase/Random.cpp:38-42`). */
export class SnowField {
  private readonly flakes: Flake[] = [];

  constructor(private readonly random: () => number) {}

  get count(): number {
    return this.flakes.length;
  }

  /** One `Deform`: move every flake, drop the spent ones, and create more
   *  around `centreMm` pushed 3,500 units along the view at its own height. */
  update(elapsedS: number, centreMm: Vec3, basis: SnowBasis, snowing: boolean): void {
    if (!snowing && this.flakes.length === 0) return;
    const cx = basis.view[0] * VIEW_OFFSET_MM + centreMm[0];
    const cy = basis.view[1] * VIEW_OFFSET_MM + centreMm[1];
    const cz = centreMm[2];
    let kept = 0;
    for (const flake of this.flakes) {
      flake.z -= flake.fallMmPerS * elapsedS; // SnowParticle.cpp:21
      flake.x += (flake.crossX * Math.sin(flake.radian)) / 10; // SnowParticle.cpp:23
      flake.y += (flake.crossY * Math.sin(flake.radian)) / 10; // SnowParticle.cpp:24
      flake.radian += flake.period * elapsedS; // SnowParticle.cpp:25
      if (flake.z < cz - FLOOR_MM) continue;
      if (Math.abs(flake.x - cx) > SNOW_DISTANCE_MM) continue;
      if (Math.abs(flake.y - cy) > SNOW_DISTANCE_MM) continue;
      this.flakes[kept++] = flake;
    }
    this.flakes.length = kept;
    if (!snowing) return;
    const spawn = Math.min(SPAWN_PER_FRAME, SNOW_MAX_FLAKES - this.flakes.length);
    for (let i = 0; i < spawn; i++) this.flakes.push(this.create(cx, cy, cz));
  }

  /** Every flake as four vertices, `position -/+ cross -/+ up` scaled by its
   *  half size with u, v at 0 or 1 (`SnowParticle.cpp:8-12,36-54`), written
   *  into `out`; returns how many flakes were written. */
  writeQuads(out: Float32Array, basis: SnowBasis): number {
    const flakes = Math.min(this.flakes.length, Math.floor(out.length / (4 * SNOW_FLAKE_FLOATS)));
    let at = 0;
    for (let i = 0; i < flakes; i++) {
      const flake = this.flakes[i];
      const h = flake.halfMm;
      flake.crossX = basis.cross[0] * h;
      flake.crossY = basis.cross[1] * h;
      for (const [s, t] of CORNERS) {
        out[at++] = flake.x + (2 * s - 1) * basis.cross[0] * h + (2 * t - 1) * basis.up[0] * h;
        out[at++] = flake.y + (2 * s - 1) * basis.cross[1] * h + (2 * t - 1) * basis.up[1] * h;
        out[at++] = flake.z + (2 * s - 1) * basis.cross[2] * h + (2 * t - 1) * basis.up[2] * h;
        out[at++] = s;
        out[at++] = t;
      }
    }
    return flakes;
  }

  private frandom(low: number, high: number): number {
    return this.random() * (high - low) + low;
  }

  private create(cx: number, cy: number, cz: number): Flake {
    const rotation = (this.frandom(0, 36000) / 100) * (Math.PI / 180); // SnowParticle.cpp:57
    const distance = (this.frandom(0, 70000) / 10) * MM; // SnowParticle.cpp:58
    return {
      x: cx + distance * Math.sin(rotation), // SnowParticle.cpp:60
      y: cy + distance * Math.cos(rotation), // SnowParticle.cpp:61
      z: cz + this.frandom(1500, 2000) * MM, // SnowParticle.cpp:62
      fallMmPerS: -this.frandom(-50, -200) * MM, // SnowParticle.cpp:65
      halfMm: this.frandom(2, 7) * MM, // SnowParticle.cpp:66-67
      period: this.frandom(1.5, 5), // SnowParticle.cpp:71
      radian: this.frandom(-1.6, 1.6), // SnowParticle.cpp:72
      crossX: 0,
      crossY: 0,
    };
  }
}

/** Vertex 1..4's (u, v), which also say which side of cross and up each
 *  corner sits on (`SnowParticle.cpp:38-52`). */
const CORNERS: readonly (readonly [number, number])[] = [[0, 0], [1, 0], [0, 1], [1, 1]];

/** The two triangles each quad is drawn with (`SnowEnvironment.cpp:267`). */
export const SNOW_QUAD_INDICES: readonly number[] = [0, 2, 1, 2, 3, 1];
